import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';

import { ALL_AXES, Axis, Database, isFiniteNumber, withLogs } from '@core';

import { mapBaseUiNode, withLimit, type BaseNode } from './uiNodes';

const UI_PARAMETERS_QUERY = `
    *,
    ui_parameters(*, ui_node_categories!swing_foundation_group(*), swing_measured_parameters(id, swing_position_in_time_series, axis_index_in_time_series, units(*), time_series_measurements(name)))
` as const;

export type ParameterNode = BaseNode & {
    categories: ParameterCategories;
    parameter: ParameterData;
};

type ParameterCategories = {
    metric: string;
    swingFoundationGroup: string | null;
};

export type ParameterData = {
    smpId: number;
    name: string;
    unit: string | undefined;
    position: string | null;
    axis: Axis | null;
    timeSeriesId: string | null | undefined;
    supportedLive: boolean;
    filterTags?: string[];
};

export const fetchUiParameters = withLogs(async (supabase: SupabaseClient<Database>): Promise<ParameterNode[]> => {
    const { data, error } = await withLimit(
        3000,
        supabase.from('ui_nodes').select(UI_PARAMETERS_QUERY).eq('type', 'parameter').order('id', { ascending: true }),
    );

    if (error) {
        console.error('error fetching ui_parameters:', error);
        return [];
    }
    const uiParams = camelcaseKeys(data, { deep: true });
    return uiParams.flatMap((uiNode) => {
        const uiParameters = uiNode.uiParameters;
        const swingMeasuredParameters = uiParameters?.swingMeasuredParameters;
        if (!uiParameters || !swingMeasuredParameters) return [];

        const axisIndex = swingMeasuredParameters.axisIndexInTimeSeries;
        const axis = isFiniteNumber(axisIndex) ? ALL_AXES[axisIndex] : null;
        const position = swingMeasuredParameters.swingPositionInTimeSeries;

        const categories: ParameterCategories = {
            metric: uiParameters.metric,
            swingFoundationGroup: uiParameters.swingFoundationGroup,
        };

        const parameter: ParameterData = {
            smpId: swingMeasuredParameters.id,
            name: uiParameters.swingMeasuredParameterName,
            unit: swingMeasuredParameters.units?.notation,
            position,
            axis,
            timeSeriesId: swingMeasuredParameters.timeSeriesMeasurements?.name,
            supportedLive: uiParameters.supportedLive,
            filterTags: (uiParameters.uiNodeCategories?.filterTags ?? []).concat(uiParameters.metric),
        };

        return [{ ...mapBaseUiNode(uiNode), categories, parameter }];
    });
}, 'fetchUiParameters');
